/**
 * O laço de frame. É o ÚNICO lugar do cliente onde o tempo de parede entra no render —
 * mesma disciplina da sala no servidor, pelo mesmo motivo: tempo espalhado é
 * impossível de testar.
 *
 * Fecha a malha de qualidade que `perf/` já tinha aberta: mede o frame, alimenta o
 * amostrador, e o controlador devolve a escala de resolução deste frame (PERFORMANCE M2).
 *
 * `requestAnimationFrame` é injetável para o teste NEVER depender de browser.
 */
import type { FrameSampler } from '../perf/frame-sampler.js';
import type { RenderScaleController } from '../perf/render-scale.js';

export interface FrameLoopOptions {
  sampler: FrameSampler;
  scale: RenderScaleController;
  /** Desenha um frame com a escala de resolução decidida para ele. */
  draw: (scale: number, deltaMs: number) => void;
  schedule?: (callback: (timestampMs: number) => void) => number;
  cancel?: (handle: number) => void;
  /**
   * Acima disto o quadro é PICO, e é avisado um a um. Ausente desliga o aviso.
   * O laço NEVER decide o número: ele sai de `BUD-frame-time` do tier, e quem
   * lê orçamento é quem monta o laço.
   */
  spikeMs?: number;
  /**
   * Um quadro estourou. `drawMs` é o desenho ANTERIOR, e NEVER o deste quadro —
   * `frameMs` é o intervalo entre duas chamadas, e o que coube dentro dele foi o
   * desenho de antes. `index` conta desde o começo e NEVER para: o teto de
   * quantos avisos viram telemetria mora em quem escuta.
   */
  onSpike?: (frameMs: number, drawMs: number, index: number) => void;
  /** Relógio do desenho. Injetável para o teste NEVER depender de browser. */
  now?: () => number;
}

/**
 * Acima disto o intervalo NEVER é um frame: é aba em segundo plano, tela bloqueada
 * ou o app voltando. Amostrar isso envenenaria o p95 e derrubaria a resolução
 * de quem só trocou de aplicativo por um instante.
 */
const MAX_SAMPLE_MS = 250;

export class FrameLoop {
  private handle: number | undefined;
  private lastTimestamp: number | undefined;
  private frameCount = 0;
  private spikeCount = 0;
  /** Duração do desenho ANTERIOR. É o que cabe dentro do intervalo medido. */
  private lastDrawMs = 0;
  private readonly now: () => number;
  private readonly schedule: (callback: (timestampMs: number) => void) => number;
  private readonly cancel: (handle: number) => void;
  /** Ligado uma vez: closure nova por frame é lixo por frame (PERFORMANCE M5). */
  private readonly boundFrame = (timestampMs: number): void => {
    this.onFrame(timestampMs);
  };

  constructor(private readonly options: FrameLoopOptions) {
    this.schedule = options.schedule ?? ((callback) => requestAnimationFrame(callback));
    this.cancel = options.cancel ?? ((handle) => {
      cancelAnimationFrame(handle);
    });
    this.now = options.now ?? ((): number => performance.now());
  }

  get running(): boolean {
    return this.handle !== undefined;
  }

  get frames(): number {
    return this.frameCount;
  }

  /** Picos desde o começo. Conta mesmo depois de quem escuta parar de gravar. */
  get spikes(): number {
    return this.spikeCount;
  }

  start(): void {
    if (this.handle !== undefined) return;
    // Sem carimbo anterior o primeiro delta seria o tempo desde o carregamento
    // da página. O primeiro frame só serve para estabelecer a origem.
    this.lastTimestamp = undefined;
    this.handle = this.schedule(this.boundFrame);
  }

  stop(): void {
    if (this.handle === undefined) return;
    this.cancel(this.handle);
    this.handle = undefined;
  }

  private onFrame(timestampMs: number): void {
    if (this.handle === undefined) return;
    this.frameCount += 1;

    const previous = this.lastTimestamp;
    this.lastTimestamp = timestampMs;
    const deltaMs = previous === undefined ? 0 : timestampMs - previous;

    if (deltaMs > 0 && deltaMs <= MAX_SAMPLE_MS) {
      this.options.sampler.push(deltaMs);
      this.options.scale.update(deltaMs);

      // O aviso sai ANTES do desenho, e com o desenho ANTERIOR: é ele que coube
      // dentro do intervalo que acabou de ser medido. Mesma porta do amostrador
      // de propósito — pico acima de MAX_SAMPLE_MS é aba em segundo plano, e
      // NEVER travada, e contá-lo encheria o laudo de gente trocando de app.
      const { spikeMs, onSpike } = this.options;
      if (spikeMs !== undefined && onSpike !== undefined && deltaMs >= spikeMs) {
        this.spikeCount += 1;
        onSpike(deltaMs, this.lastDrawMs, this.spikeCount);
      }
    }

    const drawStart = this.now();
    this.options.draw(this.options.scale.scale, deltaMs);
    this.lastDrawMs = this.now() - drawStart;
    this.handle = this.schedule(this.boundFrame);
  }
}
